import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, GitPullRequestArrow, ExternalLink } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const TYPE = { change: 'Change', new_content: 'New content', removal: 'Removal' };
const TONE = { pending: 'bg-amber-500/15 text-amber-300', approved: 'bg-emerald-500/15 text-emerald-300', rejected: 'bg-rose-500/15 text-rose-300' };
const fmt = (v) => (v ? new Date(v).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '');

function TextBox({ label, text, tone }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">{label}</p>
      <pre className={`whitespace-pre-wrap text-xs rounded-lg p-3 max-h-64 overflow-y-auto border ${tone}`}>{text}</pre>
    </div>
  );
}

function RequestCard({ r, headers, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const canApply = r.type === 'removal' || !!r.suggested_text;
  const decide = async (kind, apply = true) => {
    if (kind === 'approve' && apply && r.type === 'removal' && !window.confirm(`Remove section "${r.section_title}" from ${r.course_title}?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/change-requests/${r.request_id}/${kind}`, { note, apply }, { headers });
      toast.success(kind === 'reject' ? 'Request declined' : apply && canApply ? 'Approved and applied to the course' : 'Approved');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update request');
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-5 space-y-3" data-testid={`change-request-${r.request_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-medium">{TYPE[r.type]}: {r.course_title}{r.section_title ? <span className="text-zinc-400"> · {r.section_title}</span> : ''}</p>
          <p className="text-xs text-zinc-500">{r.requested_by_name} ({r.requested_by_role?.replace('_', ' ')}) · {fmt(r.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${TONE[r.status]}`}>{r.status === 'rejected' ? 'Declined' : r.status}{r.applied ? ' · applied' : ''}</span>
          <a href={`/courses/${r.course_id}/ai-learn?edit=true`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white" title="Open course editor" data-testid={`change-request-open-${r.request_id}`}><ExternalLink className="h-4 w-4" /></a>
        </div>
      </div>
      <p className="text-sm text-zinc-200 whitespace-pre-wrap">{r.description}</p>
      {r.suggested_text && (
        <div className={`grid gap-3 ${r.original_text ? 'md:grid-cols-2' : ''}`}>
          {r.original_text && <TextBox label="Current text" text={r.original_text} tone="border-zinc-800 bg-zinc-950 text-zinc-400" />}
          <TextBox label={r.type === 'new_content' ? `Suggested new section${r.suggested_title ? `: ${r.suggested_title}` : ''}` : 'Suggested text'} text={r.suggested_text} tone="border-emerald-500/30 bg-emerald-500/5 text-zinc-200" />
        </div>
      )}
      {r.review_note && <p className="text-xs text-zinc-400">Review note ({r.reviewed_by}, {fmt(r.reviewed_at)}): {r.review_note}</p>}
      {r.status === 'pending' && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the requester (optional)" className="flex-1 min-w-[200px] bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white" data-testid={`change-request-note-${r.request_id}`} />
          {canApply && <button onClick={() => decide('approve', true)} disabled={busy} className="btn-gold px-4 py-2 text-sm inline-flex items-center gap-1.5" data-testid={`change-request-apply-${r.request_id}`}><Check className="h-4 w-4" />{r.type === 'removal' ? 'Approve & remove' : 'Approve & apply'}</button>}
          <button onClick={() => decide('approve', false)} disabled={busy} className="px-4 py-2 text-sm rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`change-request-approve-${r.request_id}`}>{canApply ? "Approve (I'll edit by hand)" : 'Approve'}</button>
          <button onClick={() => decide('reject')} disabled={busy} className="px-4 py-2 text-sm rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-1.5" data-testid={`change-request-reject-${r.request_id}`}><X className="h-4 w-4" />Decline</button>
        </div>
      )}
    </div>
  );
}

export default function ChangeRequestsAdmin({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState({ requests: [], pending: 0 });
  const load = useCallback(() => {
    axios.get(`${API}/admin/change-requests`, { headers, params: { status: status || undefined } })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load change requests'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6 p-6 lg:p-8" data-testid="change-requests-admin">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white">Course change requests</h1>
        <p className="text-sm text-zinc-400 mt-1">Instructors and admins can't edit courses. Their requests for changes, new content and removals wait here for your approval.</p>
      </div>
      <div className="flex gap-2">
        {[['pending', `Pending (${data.pending})`], ['approved', 'Approved'], ['rejected', 'Declined'], ['', 'All']].map(([id, label]) => (
          <button key={id || 'all'} onClick={() => setStatus(id)} data-testid={`change-requests-filter-${id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${status === id ? 'border-navigate-gold text-white bg-navigate-gold/10' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>{label}</button>
        ))}
      </div>
      {data.requests.length === 0 ? (
        <div className="text-center py-14 text-zinc-500 border border-dashed border-zinc-800 rounded-xl"><GitPullRequestArrow className="h-8 w-8 mx-auto mb-2 text-zinc-600" />No requests here.</div>
      ) : data.requests.map((r) => <RequestCard key={r.request_id} r={r} headers={headers} onDone={load} />)}
    </div>
  );
}
